import { useCallback, useMemo } from 'react';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';

import {
    useCageImageController_GetDaysWithImages,
    useCageImageController_GetImageList,
} from '@/services/hooks';
import { CageImageController_Image, template } from '@/services/services';

import { asArray, toBioscopeImageListQuery } from './mappers';
import type { BioscopeImageDay, BioscopeImageMeta, UseBioscopeImagesResult } from './types';
import { useNeighbourDayPrefetch } from './useNeighbourDayPrefetch';
import { useScopeImagesToEntity, useSelectedDayForEntity } from './useScopedBioscopeImages';

export function useGetCageBioscopeImages(
    cageId: number,
    permalinkImageId?: string
): UseBioscopeImagesResult {
    const queryClient = useQueryClient();
    const { selectedDay, onDayChange } = useSelectedDayForEntity(cageId);
    const queryParams = toBioscopeImageListQuery(selectedDay, permalinkImageId);
    const enabled = cageId > 0;

    const daysQuery = useCageImageController_GetDaysWithImages(cageId, {
        enabled,
    });
    const imagesQuery = useCageImageController_GetImageList(cageId, queryParams, {
        enabled,
        placeholderData: keepPreviousData,
    });

    const days = useMemo(
        () => asArray<BioscopeImageDay>(daysQuery.data?.data),
        [daysQuery.data?.data]
    );
    const rawImages = useMemo(
        () => asArray<BioscopeImageMeta>(imagesQuery.data?.data),
        [imagesQuery.data?.data]
    );
    const { images, isLoading, isPreviousData, isError } = useScopeImagesToEntity(
        cageId,
        imagesQuery.isPlaceholderData,
        imagesQuery.isPending,
        Boolean(imagesQuery.isError),
        rawImages
    );

    const prefetch = useCallback(
        (day: string) => useCageImageController_GetImageList.prefetch(queryClient, cageId, { day }),
        [queryClient, cageId]
    );
    const readFirstImage = useCallback(
        (day: string): BioscopeImageMeta | undefined => {
            const { key } = useCageImageController_GetImageList.info(cageId, { day });
            const cached = queryClient.getQueryData(key) as { data?: unknown } | undefined;
            return asArray<BioscopeImageMeta>(cached?.data)[0];
        },
        [queryClient, cageId]
    );
    const jpegSrc = useCallback(
        (imageId: string): string =>
            template(CageImageController_Image.key, { cageId, id: imageId }),
        [cageId]
    );

    useNeighbourDayPrefetch({
        days,
        selectedDay,
        enabled,
        prefetch,
        readFirstImage,
        jpegSrc,
    });

    const { refetch: refetchImages } = imagesQuery;
    const retryImages = useCallback(() => {
        void refetchImages();
    }, [refetchImages]);

    return {
        images,
        days,
        isLoading,
        isPreviousData,
        isError,
        isFetching: imagesQuery.isFetching,
        refetch: retryImages,
        onDayChange,
    };
}
